import dynamic from 'next/dynamic';
import { useRouter } from 'next/router';
import { useState } from 'react';
import { useForm } from 'react-hook-form';
import PhoneInput from 'react-phone-input-2';

import useWindowSize from '#app/hooks/useWindowSize';
import { eventGtag } from '#app/hooks/eventGtag';
import { eventFb } from '#app/hooks/eventFb';
import { useUserContext, useUserGeolocalization } from '#hooks/index';
import { postLeadContact } from '#api/enrollment';
import { regexEmail } from '#helpers/regex';
import { studentAgeRange } from '#helpers/formsUtils';

const Toast = dynamic(() => import('#components/index').then(mod => mod.Toast));
const SelectField = dynamic(() => import('#atoms/index').then(mod => mod.SelectField));
const TextField = dynamic(() => import('#atoms/index').then(mod => mod.TextField));
const TextareaField = dynamic(() =>
  import('#atoms/index').then(mod => mod.TextareaField)
);
const Typography = dynamic(() => import('#app/UI/atoms/new-typography'));
const Button = dynamic(() => import('#app/UI/molecules/button/button'));

const Form = () => {
  const router = useRouter();
  const { data: geolocalizationData } = useUserGeolocalization();
  const {
 register, setValue, handleSubmit, errors 
} = useForm();
  const { data: userData } = useUserContext();
  const { isMobile } = useWindowSize();
  const [country, setCountry] = useState();
  const [submited, setSubmited] = useState(false);
  const [loader, setLoader] = useState(false);

  const parseLowerCase = event => setValue('email', event.target.value.toLowerCase());

  const onSubmit = async data => {
    setLoader(true);
    const params = {
      first_name: data.first_name,
      last_name: data.last_name,
      email: data.email,
      country: country.countryCode.toUpperCase(),
      country_name: country.name,
      phone_number: data.phone_number,
      age_range: data.student_age_range,
      message: data.message,
      form_type: 'contact'
    };

    if (
      data.first_name &&
      data.last_name &&
      data.email &&
      country.countryCode &&
      country.name &&
      data.phone_number &&
      data.student_age_range &&
      data.message
    ) {
      eventGtag('lead', userData, params);
      eventFb('lead', userData, params);
    } else;

    // Go to
    const pathname = '/contactanos/gracias';

    try {
      const response = await postLeadContact(params);

      if (response.status === 200 || response.status === 201) {
        router.push(pathname);
        setSubmited(true);
        setLoader(false);
      } else {
        setLoader(false);
      }
    } catch {
      setLoader(false);
    }
  };

  const handleOnChange = (value, country) => {
    setValue('phone_number', `+${value}`);
    setCountry(country);
  };

  return (
    <form onSubmit={handleSubmit(onSubmit)}>
      <div className="flex sm_flex-col gap-x-16 gap-y-16 mb-16 sm_mb-16">
        <div className="flex flex-col gap-y-8 w-full">
          <div>
            <Typography className="typo-14" tag="p">
              Nombre
            </Typography>

            <TextField
              id="first_name"
              variant="contact-us"
              maxlength="40"
              placeholder="Nombre del padre o tutor"
              name="first_name"
              size="20"
              type="text"
              register={register({ required: 'El nombre es un campo obligatorio' })}
              error={!!errors.first_name}
            />
          </div>

          {errors.first_name && <Toast message={errors.first_name.message} mtop="1px" />}
        </div>

        <div className="flex flex-col gap-y-8 w-full">
          <div>
            <Typography className="typo-14" tag="p">
              Apellido
            </Typography>

            <TextField
              id="last_name"
              maxlength="80"
              placeholder="Apellido del padre o tutor"
              name="last_name"
              size="20"
              type="text"
              variant="contact-us"
              register={register({ required: 'El apellido es un campo obligatorio' })}
              error={!!errors.last_name}
            />
          </div>
          {errors.last_name && <Toast message={errors.last_name.message} mtop="1px" />}
        </div>
      </div>

      <div className="flex sm_flex-col gap-x-16 gap-y-16 mb-16 sm_mb-16">
        <div className="flex flex-col gap-y-8 w-full">
          <div>
            <Typography className="typo-14" tag="p">
              Telefono
            </Typography>
            {geolocalizationData && (
              <PhoneInput
                inputExtraProps={{
                  name: 'phone_number',
                  required: true
                }}
                inputClass="custom-phone-input-field-contact-us"
                country={geolocalizationData.countryCode.toLowerCase()}
                onChange={handleOnChange}
                {...register('phone_number', {
                  required: 'El teléfono es un campo obligatorio',
                  minLength: {
                    value: 9,
                    message: 'El teléfono requiere un mínimo de 6 dígitos'
                  },
                  message: 'El teléfono es un campo obligatorio'
                })}
              />
            )}
          </div>

          {errors.phone_number && (
            <Toast message={errors.phone_number.message} mtop="0" />
          )}
        </div>

        <div className="flex flex-col gap-y-8 w-full">
          <div>
            <Typography className="typo-14" tag="p">
              Correo
            </Typography>
            <TextField
              id="email"
              maxlength="80"
              placeholder="Correo electrónico"
              name="email"
              onChange={parseLowerCase}
              size="20"
              type="email"
              variant="contact-us"
              register={register({
                required: 'El email es un campo obligatorio',
                pattern: {
                  value: regexEmail,
                  message: 'Esto no es un email, intenta con uno válido'
                }
              })}
              error={!!errors.email}
            />
          </div>

          {errors.email && <Toast message={errors.email.message} mtop="1px" />}
        </div>
      </div>

      <div className="flex sm_flex-col gap-x-32 gap-y-16 mb-16 sm_mb-32">
        <div className="flex flex-col gap-y-8 w-full">
          <div>
            <Typography className="typo-14" tag="p">
              Edad del estudiante:
            </Typography>
            <SelectField
              id="student_age_range"
              name="student_age_range"
              placeholder="Seleccione la edad del estudiante"
              options={studentAgeRange}
              variant="contact-us"
              register={register({ required: 'Este es un campo obligatorio' })}
              error={!!errors.student_age_range}
            />
          </div>

          {errors.student_age_range && (
            <Toast message={errors.student_age_range.message} mtop="1px" />
          )}
        </div>
      </div>

      <div className="flex sm_flex-col gap-x-32 gap-y-16 mb-16 sm_mb-16">
        <div className="flex flex-col gap-y-8 w-full">
          <div>
            <Typography className="typo-14" tag="p">
              Pregunta o mensaje
            </Typography>
            <TextareaField
              id="message"
              name="message"
              placeholder="Déjanos cualquier duda o tema del que quieras saber más."
              size="20"
              type="text"
              variant="contact-us"
              register={register({ required: 'El mensaje es un campo obligatorio' })}
              error={!!errors.message}
            />
          </div>

          {errors.message && <Toast message={errors.message.message} mtop="1px" />}
        </div>
      </div>

      <div className="flex w-full mb-12 justify-end">
        <Button
          type="submit"
          variant="black"
          width={isMobile ? '100%' : '302px'}
          height="48px"
          textOptions={{
            fontSize: [16, 16],
            lineHeight: [21, 21],
            fontWeight: 700
          }}
        >
          {!loader ? 'Enviar' : '...Cargando'}
        </Button>
      </div>

      {submited && (
        <div className="flex justify-end w-full">
          <Typography className="typo-16 text-green-1" tag="p">
            Datos de contacto enviados correctamente, pronto te contactaremos
          </Typography>
        </div>
      )}
    </form>
  );
};

export default Form;
